import { FC, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Utils } from '../../utils/common-utils';

export interface SplitPaymentLine {
  paymentMode: string;
  amount: number;
  instrumentId: string;
}

const MODES = [
  { value: 'CASH', label: 'Cash', ref: 'Receipt / Ref No. (optional)' },
  { value: 'UPI', label: 'UPI', ref: 'UPI Transaction ID' },
  { value: 'CARD', label: 'Card', ref: 'Card Txn / Approval Code' },
  { value: 'CHEQUE', label: 'Cheque', ref: 'Cheque Number' },
  { value: 'NETBANKING', label: 'Net Banking', ref: 'UTR / Reference No.' },
];

interface Props {
  open: boolean;
  payableAmount: number;
  lines: SplitPaymentLine[];
  defaultMode: string;
  defaultAmount?: number;
  defaultReference?: string;
  saveLabel?: string;
  onClose: () => void;
  onChange: (lines: SplitPaymentLine[]) => void;
  onSave: (lines: SplitPaymentLine[]) => void;
  /** When given, shows a "Save & Share" button next to save. */
  onSaveShare?: (lines: SplitPaymentLine[]) => void;
}

const SplitPaymentDialog: FC<Props> = ({ open, payableAmount, lines, defaultMode, defaultAmount, defaultReference, saveLabel, onClose, onChange, onSave, onSaveShare }) => {
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      const balance = Number((payableAmount - (Number(defaultAmount) || 0)).toFixed(2));
      setRows(lines.length ? lines.map((l) => ({ ...l })) : [
        { paymentMode: defaultMode || 'CASH', amount: Number(defaultAmount) > 0 ? Number(defaultAmount) : (payableAmount > 0 ? payableAmount : ''), instrumentId: defaultReference || '' },
        { paymentMode: defaultMode === 'UPI' ? 'CASH' : 'UPI', amount: Number(defaultAmount) > 0 && balance > 0 ? balance : '', instrumentId: '' },
      ]);
    }
  }, [open]);

  // Keep the invoice form behind the popup in sync while editing.
  useEffect(() => {
    if (open && rows.length) onChange(rows.map((r) => ({ paymentMode: r.paymentMode, amount: Number(r.amount) || 0, instrumentId: String(r.instrumentId || '').trim() })));
  }, [rows]);

  const total = Number(rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(2));
  const remaining = Number((payableAmount - total).toFixed(2));
  const hasEmptyAmount = rows.some((r) => !(Number(r.amount) > 0));
  const error = hasEmptyAmount ? 'Enter an amount for every payment method.' : remaining < 0 ? 'Total exceeds the invoice amount.' : '';

  const update = (i: number, field: string, value: any) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={greyDialogTitleSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5">Split Payment</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', mb: 0.5, pt: 3 }}>
          <Grid item xs={3}>Method</Grid>
          <Grid item xs={3}>Amount (₹)</Grid>
          <Grid item xs={5}>Transaction ID / Reference</Grid>
        </Grid>
        {rows.map((r, i) => (
          <Grid container spacing={1} key={i} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={3}>
              <Select size="small" fullWidth value={r.paymentMode} onChange={(e) => update(i, 'paymentMode', String(e.target.value))} sx={{ fontSize: 14 }}>
                {MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <TextField size="small" fullWidth type="number" value={r.amount} placeholder="0"
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                onChange={(e) => {
                  // A row can take at most what the other rows leave of the invoice amount.
                  const others = rows.reduce((sum, x, idx) => (idx === i ? sum : sum + (Number(x.amount) || 0)), 0);
                  const max = Math.max(0, Number((payableAmount - others).toFixed(2)));
                  update(i, 'amount', e.target.value === '' ? '' : Math.min(Math.max(0, Number(e.target.value)), max));
                }} />
            </Grid>
            <Grid item xs={5}>
              <TextField size="small" fullWidth value={r.instrumentId} placeholder={MODES.find((m) => m.value === r.paymentMode)?.ref}
                onChange={(e) => update(i, 'instrumentId', e.target.value)} />
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, idx) => idx !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<AddCircleOutlineOutlinedIcon />} sx={{ textTransform: 'none' }}
          onClick={() => setRows([...rows, { paymentMode: 'CASH', amount: remaining > 0 ? remaining : '', instrumentId: '' }])}>
          Add payment method
        </Button>
        <Divider sx={{ my: 1.5 }} />
        <Grid container sx={{ fontSize: 14 }} rowSpacing={0.5}>
          <Grid item xs={8}>Invoice Amount</Grid>
          <Grid item xs={4} textAlign="end">{Utils.formatNumber(payableAmount || 0, true, true)}</Grid>
          <Grid item xs={8}>Total Received</Grid>
          <Grid item xs={4} textAlign="end"><b>{Utils.formatNumber(total, true, true)}</b></Grid>
          <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
          <Grid item xs={8}>Balance Amount</Grid>
          <Grid item xs={4} textAlign="end" sx={{ color: remaining < 0 ? '#d32f2f' : '#059669' }}>{Utils.formatNumber(remaining, true, true)}</Grid>
        </Grid>
        {error && <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 8 }}>{error}</div>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform: 'none' }} disabled={!!error}
          onClick={() => onSave(rows.map((r) => ({ paymentMode: r.paymentMode, amount: Number(r.amount), instrumentId: String(r.instrumentId || '').trim() })))}>
          {saveLabel || 'Save'}
        </Button>
        {onSaveShare && <Button variant="contained" sx={{ textTransform: 'none' }} disabled={!!error}
          onClick={() => onSaveShare(rows.map((r) => ({ paymentMode: r.paymentMode, amount: Number(r.amount), instrumentId: String(r.instrumentId || '').trim() })))}>
          {saveLabel && saveLabel.startsWith('Update') ? 'Update & Share' : 'Save & Share'}
        </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default SplitPaymentDialog;
